import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../MainURL';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import CompanySubnav from './CompanySubnav';
import { useCompanyReveal } from './useCompanyReveal';
import './companyShared.scss';
import './CompanyNotice.scss';

type Notice = {
  id: number;
  category: string;
  title: string;
  content: string;
  isPinned: 0 | 1;
  createdAt: string;
};

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDisplayDate(value: string): string {
  const date = toDate(value);
  if (!date) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}·${mm}·${dd}`;
}

export default function CompanyNotice() {
  useCompanyReveal();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    axios
      .get<{ ok: boolean; data?: Notice[] }>(`${MainURL}/admin-console/notices`, {
        params: { published: 1 },
      })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.ok && Array.isArray(res.data.data)) {
          setNotices(res.data.data);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const metaText = useMemo(() => {
    if (status !== 'ready') return '';
    const latest = notices
      .map((n) => toDate(n.createdAt))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => b.getTime() - a.getTime())[0];
    const count = `${notices.length} Posts`;
    return latest ? `${count} · Updated ${MONTHS_EN[latest.getMonth()]} ${latest.getFullYear()}` : count;
  }, [notices, status]);

  return (
    <div className="company-page company-notice">
      <Header />
      <CompanySubnav />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· Company · Notice</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                소식과
                <br />
                <span className="em">업데이트</span>.
              </h1>
              <p className="page-sub">
                교회앱모아의 새로운 소식, 서비스 업데이트, 행사 안내를 확인하세요.
              </p>
            </div>
            <div className="page-meta">{metaText}</div>
          </div>
        </div>
      </section>

      <section className="notice-main">
        <div className="container">
          <ul className="notice-list reveal">
            {status === 'loading' ? <li className="notice-empty">공지사항을 불러오는 중입니다.</li> : null}
            {status === 'error' ? (
              <li className="notice-empty">공지사항을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</li>
            ) : null}
            {status === 'ready' && notices.length === 0 ? (
              <li className="notice-empty">등록된 공지사항이 없습니다.</li>
            ) : null}
            {notices.map((notice) => (
              <li className="notice-item" key={notice.id}>
                <div className="notice-meta">
                  <span className="notice-cat">{notice.category || '공지'}</span>
                  {notice.isPinned ? <span className="notice-pin">Pinned</span> : null}
                  <span className="notice-date">{formatDisplayDate(notice.createdAt)}</span>
                </div>
                <h2 className="notice-title">{notice.title}</h2>
                <p className="notice-excerpt">{notice.content}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="final-cta">
        <div className="container final-cta-inner reveal">
          <h2>
            궁금한 점이
            <br />
            <span className="em">있으신가요</span>?
          </h2>
          <p>공지와 관련된 문의는 언제든 연락 주세요.</p>
          <Link to="/inquiry" className="btn-cream">
            문의하기 &nbsp;→
          </Link>
        </div>
      </section>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
}
